import React, { useState, useCallback } from 'react';
import { Search, Loader2, UserPlus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { recipeAPI } from '@/utils/api';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';
import { t } from '@/i18n/translations';

export const SearchBar = ({ className = '' }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [statusMsg, setStatusMsg] = useState('');
    const [showAuthPrompt, setShowAuthPrompt] = useState(false);
    const navigate = useNavigate();
    const { language, getLocalizedPath } = useLanguage();
    const { t: i18nT } = useTranslation();
    const lang = language || 'en';

    const handleSearch = useCallback(async (e) => {
        e.preventDefault();
        const query = searchQuery.trim();
        if (!query || query.length < 2) return;

        setLoading(true);
        setStatusMsg('');

        // Show progressive status after a delay (AI generation takes time)
        const timer = setTimeout(() => {
            setStatusMsg('Searching deeper...');
        }, 4000);
        const timer2 = setTimeout(() => {
            setStatusMsg('Searching the recipe for you...');
        }, 8000);

        try {
            const res = await recipeAPI.search(query, lang);
            const data = res.data;

            if (data.found && data.recipes && data.recipes.length > 0) {
                const recipePath = getLocalizedPath(`/recipe/${data.recipes[0].slug}`);
                if (data.source && data.source.startsWith('generated_')) {
                    toast.success('Recipe created just for you!', { duration: 3000 });
                }
                setShowAuthPrompt(false);
                navigate(recipePath);
            } else if (data.requires_auth) {
                setShowAuthPrompt(true);
            } else {
                toast.info(i18nT('search.noResults') || 'No recipes found. Try a different search.');
            }

        } catch (error) {
            if (error.response && error.response.status === 429) {
                toast.error(i18nT('search.rateLimited') || 'Too many searches. Please wait.');
            } else if (error.code === 'ECONNABORTED') {
                toast.error('Search took too long. Please try again with a simpler query.');
            } else {
                toast.error('Search failed. Please try again.');
            }
        } finally {
            clearTimeout(timer);
            clearTimeout(timer2);
            setLoading(false);
            setStatusMsg('');
        }
    }, [searchQuery, navigate, i18nT, getLocalizedPath, lang]);

    return (
        <form onSubmit={handleSearch} className={`flex flex-col gap-2 ${className}`} data-testid="search-form">
            <div className="flex gap-2">
                <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#9C9C9C]" />
                    <Input
                        type="text"
                        placeholder={t('homepage.hero.searchPlaceholder', lang)}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        maxLength={80}
                        className="pl-12 bg-white border-[#E8E4DC] focus:border-[#6A1F2E] focus:ring-[#6A1F2E]/20 h-14 text-base font-light text-gray-900 placeholder:text-gray-400 caret-gray-900"
                        data-testid="search-input"
                        disabled={loading}
                    />
                </div>
                <Button
                    type="submit"
                    className="bg-[#6A1F2E] hover:bg-[#8B2840] text-white h-14 px-8 font-light tracking-wide"
                    data-testid="search-button"
                    disabled={loading || searchQuery.trim().length < 2}
                >
                    {loading ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {t('search.searching', lang)}
                        </>
                    ) : (
                        t('search.button', lang)
                    )}
                </Button>
            </div>
            {statusMsg && (
                <p className="text-sm text-[#6A1F2E]/70 font-light animate-pulse text-center" data-testid="search-status">
                    {statusMsg}
                </p>
            )}
            {showAuthPrompt && (
                <div className="bg-[#6A1F2E]/5 border border-[#6A1F2E]/20 rounded-lg p-4 text-center" data-testid="auth-prompt">
                    <div className="flex items-center justify-center gap-2 mb-2">
                        <UserPlus className="h-5 w-5 text-[#6A1F2E]" />
                        <p className="text-sm font-medium text-[#6A1F2E]">
                            You've discovered 5 new recipes!
                        </p>
                    </div>
                    <p className="text-xs text-[#6A1F2E]/70 mb-3">
                        Sign up for free to keep exploring unlimited recipes.
                    </p>
                    <div className="flex items-center justify-center gap-3">
                        <Link to="/signup">
                            <Button size="sm" className="bg-[#6A1F2E] hover:bg-[#8B2840] text-white h-8 text-xs" data-testid="auth-prompt-signup">
                                Sign Up Free
                            </Button>
                        </Link>
                        <Link to="/login">
                            <Button size="sm" variant="outline" className="border-[#6A1F2E]/30 text-[#6A1F2E] h-8 text-xs" data-testid="auth-prompt-login">
                                Log In
                            </Button>
                        </Link>
                    </div>
                </div>
            )}
        </form>
    );
};
